import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Menu,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as TotalIcon,
  CheckCircle as AssignedIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  Assignment as AssignmentIcon,
  PendingActions as PendingIcon,
  Refresh as ResetIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  TrendingFlat as DeltaIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import * as XLSX from 'xlsx';
import { formatAmount } from '../../../utils/global';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { appendCashReconLedgerCoaQueryParams } from '../CashRecon/cashReconLedgerScope';
import AssignedLedgersDrawer from './AssignedLedgersDrawer';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateKpiCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateToolbarBuFieldSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getPropertyId = (property) =>
  property?.propertyId ?? property?.PROPERTY_ID ?? property?.property_id ?? null;

const getPropertyLabel = (property) => {
  const code = property?.propertyCode || property?.PROPERTY_CODE || '';
  const name = property?.propertyName || property?.PROPERTY_NAME || '';
  return code && name ? `${code} - ${name}` : name || code || '';
};

const isRowAssigned = (row) => {
  const code = row?.gl_code;
  if (code != null && String(code).trim() !== '') return true;
  const status = String(row?.assignment_status || '').toLowerCase();
  return status === 'assigned';
};

const formatGridDate = (value) => {
  if (!value) return '';
  const d = typeof value === 'string' ? value.split('T')[0] : value;
  if (!d) return '';
  const [y, m, day] = String(d).split('-');
  if (y && m && day) return `${m}/${day}/${y}`;
  return String(value);
};

const getGlAccountCode = (account) =>
  String(account?.glCode ?? account?.gl_code ?? account?.glAccountNo ?? '').trim();

/** Same label format as Cash Recon → Add Mapping → GL Account. */
const getGlAccountLabel = (account) => {
  if (!account || typeof account === 'string') return account || '';
  if (account.glAccount) return account.glAccount;
  const code = getGlAccountCode(account);
  const desc = account.glDescription ?? account.gl_description ?? account.glAccountName ?? '';
  return desc ? `${code} - ${desc}` : code;
};

/** Autocomplete GL picker (Add Mapping style) for grid cells and dialogs. */
function GlAccountAutocomplete({ value, options, loading, disabled, onChange }) {
  const selected =
    options.find((account) => getGlAccountCode(account) === String(value || '').trim()) || null;

  return (
    <Box
      sx={{ width: '100%', minWidth: 0, py: 0.25 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        loading={loading}
        disabled={disabled}
        value={selected}
        onChange={(_, newValue) => onChange(newValue ? getGlAccountCode(newValue) : '')}
        getOptionLabel={getGlAccountLabel}
        isOptionEqualToValue={(a, b) => getGlAccountCode(a) === getGlAccountCode(b)}
        renderOption={(props, option) => (
          <li {...props} key={`${getGlAccountCode(option)}-${option.ledgerAccountId || option.id || ''}`}>
            {getGlAccountLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={loading ? 'Loading...' : 'Select GL Account'}
            aria-label="Select GL Account"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              ...harmonateTextFieldSx,
              '& .MuiOutlinedInput-root': {
                bgcolor: ht.white,
                borderRadius: ht.fieldRadius,
                fontSize: '0.8125rem',
              },
            }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 280 } }}
        componentsProps={{
          popper: { style: { zIndex: 2000 } },
          paper: {
            sx: {
              borderRadius: ht.fieldRadius,
              maxHeight: 280,
              '& .MuiAutocomplete-option': { fontSize: '0.875rem' },
            },
          },
        }}
      />
    </Box>
  );
}

function CustomToolbar({ onToggleSettings }) {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </Box>
      <Tooltip title="Column settings">
        <IconButton size="small" onClick={onToggleSettings}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}

const PmsTransactionGLMapping = () => {
  const { canEdit, canExport } = useFunctionPermissions(FC.PMS_RECONCILIATION);
  const { toastState, showSuccess, showError, showWarning, hideToast } = useToast();
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [showFilters, setShowFilters] = useState(true);
  const [draftFilters, setDraftFilters] = useState({
    dateFrom: '',
    dateTo: '',
    assignmentStatus: 'all',
    search: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: '',
    dateTo: '',
    assignmentStatus: 'all',
    search: '',
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [savingRowId, setSavingRowId] = useState(null);

  const [glAccounts, setGlAccounts] = useState([]);
  const [glAccountsLoading, setGlAccountsLoading] = useState(false);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkGlCode, setBulkGlCode] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const [exporting, setExporting] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [assignedLedgersOpen, setAssignedLedgersOpen] = useState(false);
  const menuOpen = Boolean(menuAnchorEl);

  const tenantUuid = localStorage.getItem('tenant_id');
  const token = localStorage.getItem('token');
  const fastApiBase = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/pms-transactions`;
  const springBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchProperties = async () => {
      const userId = localStorage.getItem('user_id');
      if (!token || !tenantUuid || !userId) {
        setProperties([]);
        return;
      }
      try {
        setPropertiesLoading(true);
        const resp = await fetch(
          `${springBase}conductor/user-tenant-properties?tenant_id=${tenantUuid}&user_id=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!resp.ok) throw new Error('Failed to load properties');
        const data = await resp.json();
        setProperties(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setProperties([]);
      } finally {
        setPropertiesLoading(false);
      }
    };
    fetchProperties();
  }, [springBase, tenantUuid, token]);

  const fetchGlAccounts = useCallback(async () => {
    if (!token || !tenantUuid) {
      setGlAccounts([]);
      return;
    }
    try {
      setGlAccountsLoading(true);
      let url =
        `${springBase}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property` +
        `?tenant_id=${encodeURIComponent(tenantUuid)}`;
      url = await appendCashReconLedgerCoaQueryParams(url, {
        baseUrl: springBase,
        tenantId: tenantUuid,
        token,
      });
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.message || 'Failed to load chart of accounts');
      }
      const data = await resp.json();
      setGlAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading chart of accounts:', err);
      setGlAccounts([]);
      showError(err.message || 'Failed to load GL codes for dropdown');
    } finally {
      setGlAccountsLoading(false);
    }
  }, [springBase, tenantUuid, token]);

  useEffect(() => {
    fetchGlAccounts();
  }, [fetchGlAccounts]);

  const fetchTransactions = useCallback(async () => {
    const propertyId = getPropertyId(selectedProperty);
    if (!token || !tenantUuid || !propertyId) {
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        tenant_uuid: tenantUuid,
        property_id: String(propertyId),
        limit: '10000',
        offset: '0',
      });
      if (appliedFilters.dateFrom) {
        params.append('transaction_date_from', appliedFilters.dateFrom);
      }
      if (appliedFilters.dateTo) {
        params.append('transaction_date_to', appliedFilters.dateTo);
      }
      if (appliedFilters.assignmentStatus && appliedFilters.assignmentStatus !== 'all') {
        params.append('assignment_status', appliedFilters.assignmentStatus);
      }

      const resp = await fetch(`${fastApiBase}/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to load PMS transactions');
      }
      const data = await resp.json();
      setRows(
        (Array.isArray(data) ? data : []).map((row) => ({
          ...row,
          id: row.id,
        }))
      );
    } catch (err) {
      console.error(err);
      showError(err.message || 'Failed to load transactions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, fastApiBase, selectedProperty, tenantUuid, token]);

  useEffect(() => {
    if (selectedProperty) {
      fetchTransactions();
    } else {
      setRows([]);
    }
  }, [selectedProperty, fetchTransactions]);

  const filteredRows = useMemo(() => {
    const term = (appliedFilters.search || '').trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const haystack = [
        formatGridDate(row.transaction_date),
        row.transaction_date,
        row.pms_code,
        row.description,
        row.amount != null ? String(row.amount) : '',
        row.transaction_type,
        row.category,
        row.department,
        row.gl_code,
        row.assignment_status,
        isRowAssigned(row) ? 'assigned' : 'unassigned',
      ]
        .filter((v) => v != null && String(v).trim() !== '')
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, appliedFilters.search]);

  const summary = useMemo(() => {
    let total = 0;
    let assigned = 0;
    let pending = 0;
    filteredRows.forEach((row) => {
      const amount = Number(row.amount) || 0;
      total += amount;
      if (isRowAssigned(row)) assigned += amount;
      else pending += amount;
    });
    return {
      total,
      assigned,
      pending,
      delta: total - assigned,
    };
  }, [filteredRows]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    const cleared = { dateFrom: '', dateTo: '', assignmentStatus: 'all', search: '' };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const updateRowGlCode = useCallback(async (rowId, glCode) => {
    if (!canEdit || !token || !tenantUuid) return;
    const trimmed = (glCode || '').trim();
    try {
      setSavingRowId(rowId);
      const params = new URLSearchParams({ tenant_uuid: tenantUuid });
      const resp = await fetch(`${fastApiBase}/${rowId}?${params.toString()}`, {
        method: trimmed ? 'PUT' : 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trimmed ? { gl_code: trimmed } : { gl_code: null }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update GL code');
      }
      const updated = await resp.json();
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...updated } : r)));
      showSuccess('GL code updated');
    } catch (err) {
      showError(err.message || 'Update failed');
    } finally {
      setSavingRowId(null);
    }
  }, [canEdit, fastApiBase, tenantUuid, token]);

  const handleBulkAssign = async () => {
    if (!canEdit || !bulkGlCode || rowSelectionModel.length === 0) return;
    try {
      setBulkSaving(true);
      const params = new URLSearchParams({ tenant_uuid: tenantUuid });
      const resp = await fetch(`${fastApiBase}/bulk/gl-code?${params.toString()}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ids: rowSelectionModel,
          gl_code: bulkGlCode,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Bulk assign failed');
      }
      showSuccess(`Assigned GL code to ${rowSelectionModel.length} transaction(s)`);
      setBulkDialogOpen(false);
      setBulkGlCode('');
      setRowSelectionModel([]);
      await fetchTransactions();
    } catch (err) {
      showError(err.message || 'Bulk assign failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleExport = () => {
    if (!canExport) return;
    try {
      setExporting(true);
      const exportRows = filteredRows.map((row) => ({
        'Transaction Date': formatGridDate(row.transaction_date),
        'PMS Code': row.pms_code,
        Description: row.description,
        Amount: row.amount,
        'Transaction Type': row.transaction_type,
        Category: row.category,
        Department: row.department,
        'GL Code': row.gl_code || '',
        'Assignment Status': row.assignment_status,
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'PMS Transactions');
      XLSX.writeFile(wb, `pms-transaction-gl-mapping-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      showError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'transaction_date',
        headerName: 'Transaction Date',
        width: 130,
        valueFormatter: (params) => formatGridDate(params.value),
      },
      { field: 'pms_code', headerName: 'PMS Code', width: 110 },
      { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (params) => formatAmount(params.value),
      },
      { field: 'transaction_type', headerName: 'Transaction Type', width: 130 },
      { field: 'category', headerName: 'Category', width: 150 },
      { field: 'department', headerName: 'Department', width: 140 },
      {
        field: 'gl_code',
        headerName: 'GL Code',
        width: 300,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <GlAccountAutocomplete
            value={params.row.gl_code || ''}
            options={glAccounts}
            loading={glAccountsLoading}
            disabled={!canEdit || glAccountsLoading || savingRowId === params.row.id}
            onChange={(code) => updateRowGlCode(params.row.id, code)}
          />
        ),
      },
      {
        field: 'assignment_status',
        headerName: 'Assignment Status',
        width: 150,
        renderCell: (params) => {
          const assigned = isRowAssigned(params.row);
          return (
            <Chip
              label={assigned ? 'Assigned' : 'Unassigned'}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
                borderRadius: ht.buttonRadius,
                bgcolor: assigned ? ht.secondaryTab : ht.listBg,
                color: assigned ? ht.text : ht.text,
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
          );
        },
      },
    ],
    [canEdit, glAccounts, glAccountsLoading, savingRowId, updateRowGlCode]
  );

  const summaryCards = [
    { title: 'Total Transaction Value', value: summary.total, icon: <TotalIcon />, color: ht.text, bg: ht.secondaryTab },
    { title: 'Assigned Value', value: summary.assigned, icon: <AssignedIcon />, color: ht.green, bg: 'rgba(2, 107, 29, 0.12)' },
    { title: 'Pending Assignment Value', value: summary.pending, icon: <PendingIcon />, color: ht.red, bg: 'rgba(239, 63, 63, 0.12)' },
    { title: 'Delta', subtitle: '(Total - Assigned)', value: summary.delta, icon: <DeltaIcon />, color: ht.text, bg: 'rgba(46, 166, 160, 0.12)' },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          PMS Reconciliation & Performance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            size="small"
            options={properties}
            value={selectedProperty}
            onChange={(_, value) => {
              setSelectedProperty(value);
              setRowSelectionModel([]);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            getOptionLabel={getPropertyLabel}
            isOptionEqualToValue={(a, b) => String(getPropertyId(a) ?? '') === String(getPropertyId(b) ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property Select"
                placeholder="Select Property"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {propertiesLoading ? <CircularProgress color="inherit" size={18} sx={{ color: ht.white }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{
              ...harmonateToolbarBuFieldSx,
              minWidth: 280,
              maxWidth: 360,
              width: 280,
              '& .MuiAutocomplete-popupIndicator': { color: ht.white },
              '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255, 255, 255, 0.75)' },
              '& .MuiAutocomplete-endAdornment': { right: 6 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'transparent',
                borderRadius: '8px !important',
                height: 40,
                alignItems: 'center',
                color: ht.white,
                paddingRight: '36px !important',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.55)',
                  borderRadius: '8px',
                },
                '&:hover fieldset': { borderColor: ht.white },
                '&.Mui-focused fieldset': { borderColor: ht.white },
              },
              '& .MuiOutlinedInput-input': {
                px: 1.25,
                fontSize: '0.875rem',
                color: ht.white,
                py: 0,
              },
            }}
            disabled={propertiesLoading}
          />
          <IconButton
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            sx={harmonatePlainIconButtonSx}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={() => setMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                borderRadius: ht.cardRadius,
                boxShadow: ht.cardShadow,
                minWidth: 180,
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null);
                if (!selectedProperty) {
                  showWarning('Select a property first');
                  return;
                }
                setAssignedLedgersOpen(true);
              }}
              disabled={!selectedProperty}
            >
              Assigned Ledgers
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1.5, mb: 2, flexShrink: 0, bgcolor: ht.white }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: showFilters ? 2 : 0 }}>
          <Tooltip title={canExport ? 'Export' : 'No export permission'}>
            <span>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <GetAppIcon />}
                onClick={handleExport}
                disabled={exporting || filteredRows.length === 0 || !canExport}
                sx={harmonateUploadOutlinedSx}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
            sx={showFilters ? harmonateProceedButtonSx : harmonateUploadOutlinedSx}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        {showFilters && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: `1px solid ${ht.line}`,
              borderRadius: ht.cardRadius,
              bgcolor: ht.listBg,
              ...harmonateFormFieldsSx,
            }}
          >
            {/* Label row — Bulk Assign aligned with filter labels */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: ht.text, fontSize: '0.875rem' }}>
                  Date From *
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: ht.text, fontSize: '0.875rem' }}>
                  Date To *
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: ht.text, fontSize: '0.875rem' }}>
                  Assignment Status
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Tooltip title={canEdit ? 'Bulk Assign GL Code' : 'No edit permission'}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<AssignmentIcon />}
                      onClick={() => {
                        if (!canEdit) return;
                        if (rowSelectionModel.length === 0) {
                          showWarning('Select at least one transaction');
                          return;
                        }
                        setBulkDialogOpen(true);
                      }}
                      disabled={!selectedProperty || !canEdit}
                      sx={harmonateUploadOutlinedSx}
                    >
                      Bulk Assign GL Code
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Input row — dates, status, search, Reset & Apply on one line */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={draftFilters.dateFrom}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                  sx={harmonateTextFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={draftFilters.dateTo}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
                  sx={harmonateTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={draftFilters.assignmentStatus}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, assignmentStatus: e.target.value }))}
                    MenuProps={harmonateSelectMenuProps}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="assigned">
                      <Box component="span" sx={{ color: ht.text, fontWeight: 600 }}>
                        Assigned
                      </Box>
                    </MenuItem>
                    <MenuItem value="unassigned">
                      <Box component="span" sx={{ color: ht.red, fontWeight: 600 }}>
                        Unassigned
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search transactions..."
                  value={draftFilters.search}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, search: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApplyFilters();
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon fontSize="small" sx={{ color: ht.text }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={harmonateTextFieldSx}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button variant="outlined" startIcon={<ResetIcon />} onClick={handleResetFilters} sx={harmonateCancelButtonSx}>
                  Reset
                </Button>
                <Button variant="contained" onClick={handleApplyFilters} disabled={!selectedProperty} sx={harmonateProceedButtonSx}>
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mb: 2,
          bgcolor: ht.white,
        }}
      >
        {!selectedProperty ? (
          <Box sx={{ p: 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: ht.text }}>Select a property to load PMS transactions.</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, height: '100%', width: '100%', overflow: 'hidden' }}>
            <DataGridPro
              rows={filteredRows}
              columns={columns}
              loading={loading}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={setRowSelectionModel}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              rowHeight={56}
              sx={{
                ...harmonateDataGridSx,
                height: '100%',
                width: '100%',
                '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab },
              }}
              slots={{
                toolbar: () => <CustomToolbar onToggleSettings={() => setShowFilters((v) => !v)} />,
              }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: 'Records per page:',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
        }}
      >
        {summaryCards.map((card) => (
          <Card
            key={card.title}
            elevation={0}
            sx={{
              ...harmonateKpiCardSx,
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 0' },
              minWidth: 0,
              minHeight: 72,
              p: 0,
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                height: '100%',
                '&:last-child': { pb: 1 },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: ht.fieldRadius,
                  bgcolor: card.bg,
                  color: card.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: ht.text }} display="block" noWrap>
                  {card.title}
                </Typography>
                {card.subtitle ? (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ fontSize: '0.65rem', lineHeight: 1.2, color: ht.fadedText }}
                    noWrap
                  >
                    {card.subtitle}
                  </Typography>
                ) : (
                  <Box sx={{ height: 14 }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                  {formatAmount(card.value)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <AssignedLedgersDrawer
        open={assignedLedgersOpen}
        onClose={() => setAssignedLedgersOpen(false)}
        selectedProperty={selectedProperty}
        dateFrom={appliedFilters.dateFrom}
        dateTo={appliedFilters.dateTo}
        glAccounts={glAccounts}
      />

      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkSaving && setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Bulk Assign GL Code</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Typography variant="body2" sx={{ mb: 2, color: ht.text }}>
            Assign the same GL code to {rowSelectionModel.length} selected transaction(s).
          </Typography>
          <FormControl fullWidth size="small">
            <GlAccountAutocomplete
              value={bulkGlCode}
              options={glAccounts}
              loading={glAccountsLoading}
              disabled={glAccountsLoading}
              onChange={setBulkGlCode}
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkSaving} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBulkAssign} disabled={!bulkGlCode || bulkSaving} sx={harmonateProceedButtonSx}>
            {bulkSaving ? <CircularProgress size={22} sx={{ color: ht.white }} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default PmsTransactionGLMapping;
